"use client";

import { MinusIcon, PlusIcon, Trash2Icon } from "lucide-react";
import Image from "next/image";
import { Button } from "@/components/ui/button";
import { cn, formatPrice, isSaleProduct } from "@/lib/utils";
import { StoreCartLineItem, StoreOrderLineItem, StoreProduct } from "@medusajs/types";
import { Locales } from "@/lib/i18n";
import { useRemoveCartItem, useUpdateCartItem } from "@/hooks/useCartMutations";
import { __, _x } from "@/lib/i18n/functions";
import { Toast, toast } from "@/components/atoms/toast";
import { ProductDocument } from "@/prismicio-types";
import ProductBadge from "@/components/atoms/product-badge";
import { formatProductQuantity } from "@/lib/product-quantity-label";
import { sanitizeImageUrl } from "@/components/molecules/image-gallery";
import { useEffect, useState } from "react";
import ProductConfigurationModal from "@/components/modules/account/product-configuration-modal";
import { Badge } from "@/components/ui/badge";
import {
  getCartQuantityStep,
  normalizeConditioning,
  toPieceQuantity,
  resolveProductConditioning,
  toLotPrice,
  toLotQuantity,
  toPiecePrice,
} from "@/lib/product-units";

type CartProductItemProps = {
  medusaProduct: StoreProduct;
  count: number;
  setCount: React.Dispatch<React.SetStateAction<number>>;
  locale: Locales;
  prismicProduct?: ProductDocument;
  className?: string;
  hideControls?: boolean;
  orderQuantity?: number;
  isFullImage?: boolean;
} & ({ cartItem: StoreCartLineItem; orderItem?: never } | { orderItem: StoreOrderLineItem; cartItem?: never });

const CartProductItem = ({
  medusaProduct,
  count,
  setCount,
  locale,
  prismicProduct,
  className,
  orderQuantity,
  hideControls = false,
  isFullImage = false,
  ...props
}: CartProductItemProps) => {
  const item = props.cartItem || props.orderItem;
  const isCartItem = !!props.cartItem;
  const productConditioning = resolveProductConditioning(
    medusaProduct?.metadata?.conditioning,
    medusaProduct?.metadata?.conditioning_bac
  );
  const [inputValue, setInputValue] = useState(toLotQuantity(count, productConditioning).toString());
  const [isEditing, setIsEditing] = useState(false);

  useEffect(() => {
    if (!isEditing) {
      setInputValue(toLotQuantity(count, productConditioning).toString());
    }
  }, [count, isEditing, productConditioning]);

  const { removeItem } = useRemoveCartItem({
    onSuccess: () => {
      toast.success({ title: __("Item removed from cart!", locale) });
    },
    onError: (error) => {
      console.error("Error removing item from cart:", error, item);
      toast.error({ title: __("Error removing item from cart", locale) });
    },
  });

  const { updateQuantity } = useUpdateCartItem({
    onError: (err) => {
      console.error("Error updating quantity", err);
      toast.error({ title: __("Failed to update quantity", locale) });
    },
  });

  const handleQuantityChange = (newQuantity: number) => {
    if (isCartItem && props.cartItem?.id && newQuantity !== props.cartItem.quantity) {
      updateQuantity(props.cartItem.id, newQuantity);
    }
  };

  const handleDecrease = () => {
    if (!isCartItem) return;
    setCount((prevCount) => {
      const newCount = prevCount > quantityStep ? prevCount - quantityStep : prevCount;
      handleQuantityChange(newCount);
      return newCount;
    });
  };

  const handleIncrease = () => {
    if (!isCartItem) return;
    setCount((prevCount) => {
      const newCount = prevCount + quantityStep;
      handleQuantityChange(newCount);
      return newCount;
    });
  };

  const handleRemove = () => {
    if (isCartItem && props.cartItem?.id) {
      removeItem(props.cartItem.id);
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    if (value === "" || /^\d+$/.test(value)) {
      setInputValue(value);
    }
  };

  const handleInputBlur = () => {
    setIsEditing(false);
    const numValue = parseInt(inputValue) || 1;
    const clampedValue = toPieceQuantity(numValue, productConditioning);
    setCount(clampedValue);
    setInputValue(toLotQuantity(clampedValue, productConditioning).toString());
    handleQuantityChange(clampedValue);
  };

  const handleInputKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter") {
      e.currentTarget.blur();
    } else if (e.key === "Escape") {
      setInputValue(toLotQuantity(count, productConditioning).toString());
      setIsEditing(false);
      e.currentTarget.blur();
    }
  };

  const handleInputFocus = () => {
    setIsEditing(true);
    setTimeout(() => {
      const input = document.activeElement as HTMLInputElement;
      if (input) input.select();
    }, 0);
  };

  if (!item) {
    return null;
  }

  // Product metadata
  const quantityStep = getCartQuantityStep(productConditioning);
  const physicalPieceCount = normalizeConditioning(productConditioning);
  const isRange = medusaProduct?.metadata?.is_surface_based as boolean;
  const isPriceDynamic = medusaProduct?.metadata?.is_price_dynamic as boolean;
  const hasMultipleVariants = (medusaProduct?.variants?.length ?? 0) > 1;

  // Variant from cart (ground truth)
  // For order items we fall back to variants[0] since no variant_id is available.
  const cartVariantId = props.cartItem?.variant_id ?? props.orderItem?.variant_id;
  const cartVariant = hasMultipleVariants
    ? medusaProduct?.variants?.find((v) => v.id === cartVariantId)
    : medusaProduct?.variants?.[0];

  // ── Surface of the cart variant — passed as defaultSurface to the modal ──
  // Ensures the configuration modal always opens showing the variant actually
  // in the cart, not a stale Zustand store value from a previous PDP visit.
  const cartVariantSurface = cartVariant?.metadata?.["surface"] as number | undefined;

  const cartVariantPrice = cartVariant?.calculated_price?.calculated_amount ?? 0;
  const singleVariantPrice = medusaProduct?.variants?.[0]?.calculated_price?.calculated_amount || 0;
  const productPrice = hasMultipleVariants ? cartVariantPrice : singleVariantPrice;
  const pricePerPiece = toPiecePrice(productPrice, productConditioning);
  const lotQuantity = toLotQuantity(count, productConditioning);

  const value = formatPrice(toLotPrice(singleVariantPrice, productConditioning));

  // Formatted prices (used in desktop list item)
  const formattedVariantUnitPrice = hasMultipleVariants
    ? formatPrice(toLotPrice(cartVariantPrice, productConditioning), { showDecimals: true })
    : value;

  const formattedVariantTotalPrice = hasMultipleVariants
    ? formatPrice(cartVariantPrice * count, { showDecimals: true })
    : formatPrice(singleVariantPrice * count);

  // Badge label for "Price per unit" when count > 1
  const selectedVariantPriceBadge = hasMultipleVariants ? formattedVariantUnitPrice : value;

  return (
    <article className={cn("flex w-full items-center gap-[23px]", className)} aria-label="Product item">
      <figure className="bg-product-card relative flex size-[6.875rem] shrink-0 items-center justify-center overflow-hidden rounded-lg border-none">
        {isFullImage ? (
          <Image
            className="absolute object-cover mix-blend-multiply"
            alt={prismicProduct?.data.title || medusaProduct.title}
            fill
            src={
              sanitizeImageUrl(medusaProduct?.images?.at(0)?.url || "") ||
              prismicProduct?.data.images[0]?.image.url ||
              prismicProduct?.data.featuredImage.url ||
              ""
            }
          />
        ) : (
          <div className="relative size-20">
            <Image
              className="absolute object-contain mix-blend-multiply"
              alt={prismicProduct?.data.title || medusaProduct.title}
              fill
              src={
                sanitizeImageUrl(medusaProduct?.images?.at(0)?.url || "") ||
                prismicProduct?.data.images[0]?.image.url ||
                prismicProduct?.data.featuredImage.url ||
                ""
              }
            />
          </div>
        )}
        {isSaleProduct(medusaProduct) && (
          <Badge className="absolute top-2 -left-6 w-[90px] -rotate-45 rounded-none" variant="festi">
            {__("To sell", locale)}
          </Badge>
        )}
      </figure>

      <div className="flex w-full flex-col items-start justify-center gap-1">
        <div className="flex w-full flex-col items-start justify-center gap-1">
          <div className="flex w-full flex-col items-start gap-2">
            <h5 className="text-blue-dark font-cabinet-grotesk w-full text-lg leading-5 font-medium break-words whitespace-pre-wrap">
              {prismicProduct?.data.title}
            </h5>

            {(isRange || isPriceDynamic) && (
              <div className="mb-1 flex w-full items-center justify-between gap-1">
                {hasMultipleVariants ? (
                  <>
                    {/* Price derived from the cart variant — store is never consulted */}
                    <span className="text-dark-primary font-cabinet-grotesk flex-1 text-base leading-3.5 font-semibold">
                      {formattedVariantTotalPrice}
                    </span>
                    {!hideControls && isCartItem && count > 1 && (
                      <ProductBadge
                        text={_x("Price : {{ selectedVariantPriceBadge }}", { selectedVariantPriceBadge }, locale)}
                      />
                    )}
                  </>
                ) : (
                  <Toast
                    id="product-hero"
                    type="info"
                    className="min-[384px]:min-w-none h-fit w-fit max-w-none py-2 shadow-none"
                    title={__("Price on demand", locale)}
                  />
                )}
                {!hideControls && isRange && prismicProduct && (
                  <ProductConfigurationModal
                    products={[prismicProduct]}
                    onlyIcon
                    medusaProducts={{ [medusaProduct.id]: medusaProduct }}
                    locale={locale}
                    // Seed the modal with the surface actually in the cart so the
                    // dropdown reflects the current cart state, not a stale store value.
                    defaultSurface={cartVariantSurface}
                    hideWhenConfigured
                  />
                )}
                {hideControls && (
                  <span>
                    {orderQuantity} {__("item", locale)}
                  </span>
                )}
              </div>
            )}

            {!isRange && !isPriceDynamic && (
              <div className="flex h-6 w-full items-center justify-between">
                <div className="flex w-full items-center justify-between">
                  <span className="text-dark-primary font-cabinet-grotesk flex-1 text-base leading-3.5 font-semibold">
                    {formatPrice((medusaProduct.variants?.at(0)?.calculated_price?.calculated_amount || 0) * count)}
                  </span>
                  {!hideControls && isCartItem && count > 1 && (
                    <ProductBadge text={_x("Price : {{ value }}", { value }, locale)} />
                  )}
                  {hideControls && physicalPieceCount > 1 && (
                    <span className="flex items-center gap-1">
                      <ProductBadge
                        className="ml-auto"
                        text={`${lotQuantity} ${lotQuantity === 1 ? __("batch", locale) : __("batches", locale)}`}
                      />
                      <ProductBadge text={_x("Price : {{ value }}", { value }, locale)} />
                    </span>
                  )}
                </div>
                {hideControls && productConditioning === 1 && (
                  <span className="ml-auto w-fit min-w-22 text-right font-medium">
                    {orderQuantity} {orderQuantity && orderQuantity > 1 ? __("items", locale) : __("item", locale)}
                  </span>
                )}
              </div>
            )}
          </div>

          {hideControls && productConditioning === 1 && count > 1 && !isRange && !isPriceDynamic && (
            <ProductBadge text={_x("Price : {{ value }}", { value }, locale)} />
          )}

          {hideControls && hasMultipleVariants && isRange && (
            <ProductBadge text={_x("Price : {{ selectedVariantPriceBadge }}", { selectedVariantPriceBadge }, locale)} />
          )}

          {!isRange && !isPriceDynamic && (
            <dl className="flex w-full items-center justify-between">
              <div className="flex w-full items-center gap-1.25">
                {physicalPieceCount > 1 && (
                  <>
                    <dt className="sr-only">{__("Price per piece", locale)}</dt>
                    <dd className="text-primary-light flex-1 text-xs leading-3.5 font-normal">
                      {formatPrice(pricePerPiece, {
                        minimumFractionDigits: 2,
                        maximumFractionDigits: 2,
                      })}
                      /{__("piece", locale)}
                    </dd>
                  </>
                )}
              </div>
              {physicalPieceCount > 1 && (
                <ProductBadge className="max-w-full whitespace-normal text-right" text={formatProductQuantity(count, productConditioning, locale)} />
              )}
            </dl>
          )}

          {!hideControls && isCartItem && (
            <div className="flex w-full items-center justify-between gap-2.5">
              <div className="flex w-full items-center gap-1.25">
                <Button
                  onClick={handleDecrease}
                  variant="outline"
                  size="icon"
                  className="border-primary-light/23 size-7.5 cursor-pointer rounded-full bg-white p-0"
                  disabled={count <= quantityStep}
                >
                  <MinusIcon size={18} />
                </Button>

                <div
                  role="status"
                  aria-live="polite"
                  aria-atomic="true"
                  className="border-primary-light/23 flex h-7.5 w-full max-w-[7.125rem] flex-1 flex-shrink-0 items-center justify-center rounded-lg border bg-white"
                >
                  <span className="text-dark-primary flex w-full items-center justify-center text-center text-base font-semibold tabular-nums">
                    <input
                      size={1}
                      type="text"
                      inputMode="numeric"
                      pattern="[0-9]*"
                      value={inputValue}
                      onChange={handleInputChange}
                      onBlur={handleInputBlur}
                      onKeyDown={handleInputKeyDown}
                      onFocus={handleInputFocus}
                      aria-live="polite"
                      aria-atomic="true"
                      aria-label={__("Quantity", locale)}
                      className="w-full border-none bg-transparent text-center font-semibold outline-none focus:ring-0 md:text-base"
                      min={1}
                    />
                  </span>
                </div>

                <Button
                  onClick={handleIncrease}
                  variant="outline"
                  size="icon"
                  className="border-primary-light/23 size-7.5 cursor-pointer rounded-full p-0"
                >
                  <PlusIcon size={18} />
                </Button>
              </div>

              <button onClick={handleRemove} className="group focus:outline-pink-primary -ml-1 size-7 rounded-full p-1">
                <Trash2Icon
                  size={20}
                  aria-hidden="true"
                  className="text-primary-light group-focus:text-pink-primary hover:text-pink-primary transition-all duration-200 hover:cursor-pointer"
                />
              </button>
            </div>
          )}
        </div>
      </div>
    </article>
  );
};

export default CartProductItem;
